import { SellBanner } from "@/components/shop/SellBanner";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Layers, Package, Sparkles, Mail, Gift } from "lucide-react";
import { HeroCards } from "@/components/shop/HeroCards";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/shop/ProductCard";
import { ServicesGrid } from "@/components/shop/Brand";
import { GameLogos } from "@/components/shop/GameLogos";
import { FollowSection } from "@/components/shop/Socials";
import { Trending, UpcomingEvents, GameRow, NewsletterSupport } from "@/components/shop/HomeSections";
import { useContent, HOME_DEFAULT } from "@/lib/content";

const CatTile = ({ to, icon: Icon, title, text, color, images, delay = 0, testid }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    let id;
    const t = setTimeout(() => { id = setInterval(() => setIdx((i) => (i + 1) % images.length), 4500); }, delay);
    return () => { clearTimeout(t); clearInterval(id); };
  }, [images.length, delay]);
  return (
  <Link to={to} data-testid={testid} className="sticker-btn group relative isolate overflow-hidden rounded-2xl bg-black p-4 text-white sm:p-8">
    {images.map((src, i) => (
      <img key={src} src={src} alt="" aria-hidden data-active={i === idx} className={`absolute inset-0 -z-20 h-full w-full scale-110 object-cover blur-[2px] transition-[opacity,transform] [transition-duration:1200ms] ease-in-out group-hover:scale-125 ${i === idx ? "opacity-100" : "opacity-0"}`} />
    ))}
    <span className="absolute inset-0 -z-10 bg-gradient-to-br from-black/80 via-black/70 to-black/60" aria-hidden />
    <span className={`flex h-10 w-10 items-center justify-center rounded-xl border-2 border-black sm:h-14 sm:w-14 sm:rounded-2xl ${color}`}><Icon className="h-5 w-5 text-black sm:h-7 sm:w-7" /></span>
    <p className="mt-4 font-display text-base leading-tight text-white sm:mt-8 sm:text-2xl">{title}</p>
    <p className="mt-1 hidden text-sm text-white/85 sm:block">{text}</p>
    <ArrowRight className="mt-3 h-4 w-4 text-white transition-transform group-hover:translate-x-1 sm:mt-6 sm:h-5 sm:w-5" />
  </Link>
  );
};

const tiles = (k, n) => Array.from({ length: n }, (_, i) => `/tiles/${k}${i ? i + 1 : ""}.webp`);

const SectionHead = ({ eyebrow, title, color = "text-amber-600", children }) => (
  <div className="mb-8 flex items-end justify-between gap-4">
    <div>
      <p className={`font-display text-sm uppercase tracking-[0.2em] ${color}`}>{eyebrow}</p>
      <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">{title}</h2>
    </div>
    {children}
  </div>
);

export default function HomePage() {
  const [latest, setLatest] = useState([]);
  const home = useContent("/content/home", HOME_DEFAULT) || HOME_DEFAULT;

  useEffect(() => {
    api.get("/products", { params: { limit: 8, in_stock: true } }).then((r) => setLatest(r.data));
  }, []);

  return (
    <div>
      <HeroCards home={home} />

      <section className="mx-auto mb-10 mt-10 md:mb-16 md:mt-16 max-w-[1440px] px-4 sm:px-6 lg:px-12" data-testid="home-games-section">
        <SectionHead eyebrow="Scegli il tuo gioco" title="I giochi che trattiamo" />
        <GameLogos testid="home-game-logo" />
      </section>

      <Trending />

      <section className="mx-auto mt-14 md:mt-24 max-w-[1440px] px-4 sm:px-6 lg:px-12" data-testid="home-latest-section">
        <SectionHead eyebrow={home.latest_eyebrow} title={home.latest_title} color="text-cyan-600" />
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6 lg:grid-cols-4" data-testid="latest-grid">
          {latest.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
        </div>
      </section>

      <section className="mx-auto mt-14 grid max-w-[1440px] grid-cols-2 gap-3 px-4 md:mt-24 sm:gap-6 sm:px-6 lg:grid-cols-4 lg:px-12">
        <CatTile to="/catalogo?category=sigillati" icon={Package} title="Prodotti Sigillati" text="Booster box, display, ETB — anche all'ingrosso" color="bg-amber-500" images={tiles("sigillati", 3)} delay={0} testid="tile-sigillati" />
        <CatTile to="/catalogo?category=singole" icon={Sparkles} title="Carte Singole" text="Condizioni verificate, pre-grading disponibile" color="bg-rose-500" images={tiles("singole", 3)} delay={1100} testid="tile-singole" />
        <CatTile to="/catalogo?category=accessori" icon={Layers} title="Accessori" text="Sleeves, binder, deck box, toploader" color="bg-cyan-500" images={tiles("accessori", 3)} delay={2200} testid="tile-accessori" />
        <CatTile to="/catalogo?category=gadget" icon={Gift} title="Gadget e Regali" text="Idee regalo nerd e molto altro" color="bg-violet-500" images={tiles("gadget", 2)} delay={3300} testid="tile-gadget" />
      </section>

      <GameRow game="Magic" eyebrow="Magic: The Gathering" />
      <GameRow game="Pokémon" eyebrow="Pokémon TCG" />

      <SellBanner testid="home-sell-banner" className="mt-14 md:mt-24" />

      <section className="mx-auto mt-14 md:mt-24 max-w-[1440px] px-4 sm:px-6 lg:px-12" data-testid="services-section">
        <SectionHead eyebrow="Card Source" title="I nostri servizi" color="text-violet-500" />
        <ServicesGrid />
      </section>

      <NewsletterSupport />
      <FollowSection />

      <section className="mx-auto mt-14 md:mt-24 max-w-[1440px] px-4 sm:px-6 lg:px-12">
        <div className="box-pattern sticker grid items-center gap-8 rounded-3xl p-8 text-black md:grid-cols-[1fr_auto] md:p-12" data-testid="home-contact-cta">
          <div>
            <h2 className="font-display text-3xl font-bold sm:text-4xl" data-testid="home-contact-title">{home.contact_title}</h2>
            <p className="mt-2 max-w-xl font-semibold text-black/70">{home.contact_text}</p>
          </div>
          <Link to="/assistenza" data-testid="home-contact-button" className="sticker-btn flex items-center gap-2 rounded-full bg-black px-7 py-3.5 font-display font-semibold text-white"><Mail className="h-4 w-4" />Assistenza e contatti</Link>
        </div>
      </section>

      <UpcomingEvents />
    </div>
  );
}
